import { ReactElement, useEffect, useRef, useState } from 'react'
import { GetSurveyResponse, SurveyQuestion, SurveyAnswerMap, SurveyQuestionChoice } from '../../models/global.types'
import { SurveyQuestionType } from '../../enums/enums'
import apis from '../../libs/services'
import { Button, LoadingSpinner, Text } from '../atoms'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import { ControlHighlight, KeyboardControl } from '../molecules/KeyboardControls'
import { HighlightEffect } from '../../enums/KeyboardControls'
import HexChoiceButton from '../atoms/HexChoiceButton/HexChoiceButton'
import AnonymityCertification from './AnonymityCertification'
import AnonymityTags from './AnonymityTags'

type SurveyStep = 'certification' | 'tags' | 'survey'

interface SurveyProps {
  surveyId: string
  onFinish?: () => void
}

const kbControlsLayer = 'survey'
const surveyButtonId = 'survey-button-next'
const surveyPageClass = 'bg-dopage-black min-h-screen flex flex-col items-center justify-center font-primary'
const surveyCardClass = 'bg-dopage-charcoalGray p-8 w-[600px] h-[750px] flex flex-col items-center justify-center'

const getQuestionInputId = (questionId: string): string => `survey-input-${questionId}`
const getQuestionLabelId = (questionId: string): string => `survey-label-${questionId}`
const getQuestionRequiredMessageId = (questionId: string): string => `survey-required-${questionId}`

function Survey ({ surveyId, onFinish }: SurveyProps): ReactElement {
  const keyboardControls = useKeyboardControls()
  const [step, setStep] = useState<SurveyStep>('certification')
  const [surveyResponseId, setSurveyResponseId] = useState<string | null>(null)
  const [surveyData, setSurveyData] = useState<GetSurveyResponse | null>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [answers, setAnswers] = useState<Record<string, SurveyAnswerMap>>({})
  const [shouldFocusInput, setShouldFocusInput] = useState(true)
  const textAnswerDebounceRef = useRef<NodeJS.Timeout | null>(null)

  // Push layer for keyboard controls
  useEffect(() => {
    keyboardControls.pushActiveLayer(kbControlsLayer)
    return () => keyboardControls.resetDefaultLayer()
  }, [keyboardControls])

  const handleCertificationContinue = (): void => {
    setStep('tags')
  }

  const handleTagsSubmit = async (anonTag: string): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const startRes = await apis.startSurvey(surveyId, anonTag)
      const responseId = startRes.data.surveyResponseId
      setSurveyResponseId(responseId)

      const surveyRes = await apis.getSurveyWithProgress(surveyId, responseId)
      setSurveyData(surveyRes.data)
      setStep('survey')
    } catch (err: any) {
      const errorMessage = (err instanceof Error) ? err.message : 'Failed to start survey'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  // Set focus on question input when question changes
  useEffect(() => {
    if (!shouldFocusInput || surveyData === null || surveyData.questions.length === 0) return
    const inputId = getQuestionInputId(surveyData.questions[currentIndex].id)
    keyboardControls.setFocusedElement(inputId, kbControlsLayer)
    setShouldFocusInput(false)
  }, [shouldFocusInput, surveyData, currentIndex, keyboardControls])

  const updateAnswer = (questionId: string, answer: SurveyAnswerMap): void => {
    const newAnswers = { ...answers, [questionId]: answer }
    setAnswers(newAnswers)
  }

  // Render certification step
  if (step === 'certification') {
    return (
      <div className={surveyPageClass}>
        <div className={surveyCardClass}>
          <AnonymityCertification onContinue={handleCertificationContinue} />
        </div>
      </div>
    )
  }

  // Render tags step
  if (step === 'tags') {
    if (loading) {
      return (
        <div className={surveyPageClass}>
          <div className={surveyCardClass}>
            <LoadingSpinner />
          </div>
        </div>
      )
    }

    if (error !== null) {
      return (
        <div className={surveyPageClass}>
          <div className={surveyCardClass}>
            <Text text={error} size='LG' className='text-red-500 mb-4' />
            <AnonymityTags onSubmit={handleTagsSubmit} />
          </div>
        </div>
      )
    }

    return (
      <div className={surveyPageClass}>
        <div className={surveyCardClass}>
          <AnonymityTags onSubmit={handleTagsSubmit} />
        </div>
      </div>
    )
  }

  // Survey step - show loading if still loading
  if (loading) {
    return (
      <div className={surveyPageClass}>
        <div className={surveyCardClass}>
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  // Survey step - show error if any
  if (error !== null) {
    return (
      <div className={surveyPageClass}>
        <div className={`${surveyCardClass}`}>
          <Text text={error} size='LG' className='text-red-500' />
        </div>
      </div>
    )
  }

  // Survey step - no data
  if (surveyData === null || surveyData.questions.length === 0) {
    return (
      <div className={surveyPageClass}>
        <div className={`${surveyCardClass}`}>
          <Text text='No survey found' size='LG' className='text-dopage-lightGreen' />
        </div>
      </div>
    )
  }

  const currentQuestion = surveyData.questions[currentIndex]
  const isLastQuestion = currentIndex === surveyData.questions.length - 1
  const progressPercent = ((currentIndex + 1) / surveyData.questions.length) * 100

  const hasValidAnswerForQuestion = (questionId: string): boolean => {
    const answer = answers[questionId]
    if (answer == null) return false
    const textLength = answer.textResponse?.trim().length ?? 0
    const choiceCount = answer.choiceIds?.length ?? 0
    return textLength > 0 || choiceCount > 0
  }
  const isNextDisabled = currentQuestion.isRequired && !hasValidAnswerForQuestion(currentQuestion.id)

  const renderRequiredMessage = (question: SurveyQuestion): ReactElement | null => {
    if (!question.isRequired || hasValidAnswerForQuestion(question.id)) return null
    const requiredMessageId = getQuestionRequiredMessageId(question.id)
    return (
      <p
        id={requiredMessageId}
        aria-live='polite'
        className='mt-2 text-sm text-red-400'
      >
        Answer required.
      </p>
    )
  }

  const handleNext = (): void => {
    if (isLastQuestion || surveyResponseId === null) return
    const answer = answers[currentQuestion.id]
    if (answer !== undefined) {
      // Fire and forget - don't block UI
      // Build request - only include textResponse if it has a value (for text questions)
      const request: { textResponse?: string, choiceIds?: string[] } = {
        choiceIds: answer.choiceIds
      }
      const textResponse = answer.textResponse?.trim()
      if (textResponse !== undefined && textResponse !== '') {
        request.textResponse = textResponse
      }
      void apis.submitSurveyAnswer(surveyId, surveyResponseId, currentQuestion.id, request)
    }
    setCurrentIndex(prev => prev + 1)
    setShouldFocusInput(true)
  }

  const handleFinish = async (): Promise<void> => {
    if (surveyResponseId === null) return
    const answer = answers[currentQuestion.id]
    if (answer !== undefined) {
      // Build request - only include textResponse if it has a value (for text questions)
      const request: { textResponse?: string, choiceIds?: string[] } = {
        choiceIds: answer.choiceIds
      }
      const textResponse = answer.textResponse?.trim()
      if (textResponse !== undefined && textResponse !== '') {
        request.textResponse = textResponse
      }
      await apis.submitSurveyAnswer(surveyId, surveyResponseId, currentQuestion.id, request)
    }
    await apis.completeSurvey(surveyId, surveyResponseId)

    if (onFinish != null) {
      onFinish()
    }
  }

  const renderQuestionInputSectionText = (question: SurveyQuestion): ReactElement => {
    const currentAnswer = answers[question.id]?.textResponse ?? ''
    const inputId = getQuestionInputId(question.id)
    const requiredMessageId = getQuestionRequiredMessageId(question.id)
    const isInvalidRequired = question.isRequired && !hasValidAnswerForQuestion(question.id)
    const describedByIds: string[] = []
    if (isInvalidRequired) describedByIds.push(requiredMessageId)
    const ariaDescribedBy = describedByIds.length > 0 ? describedByIds.join(' ') : undefined

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
      const value = e.target.value
      if (textAnswerDebounceRef.current !== null) clearTimeout(textAnswerDebounceRef.current)
      textAnswerDebounceRef.current = setTimeout(() => {
        updateAnswer(question.id, { textResponse: value, choiceIds: [] })
      }, 300)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
      if (e.key === 'Tab') {
        e.preventDefault()
        if (e.shiftKey) {
          keyboardControls.focusPreviousElement()
        } else {
          keyboardControls.focusNextElement()
        }
      }
    }

    return (
      <>
        <ControlHighlight
          active={keyboardControls.isEnabled && keyboardControls.isElementFocused(inputId)}
          highlightEffect={HighlightEffect.InputOutline}
        >
          <KeyboardControl layer={kbControlsLayer} manualFocusHandling>
            <textarea
              key={question.id}
              id={inputId}
              aria-describedby={ariaDescribedBy}
              aria-required={question.isRequired}
              aria-invalid={isInvalidRequired}
              defaultValue={currentAnswer}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder='Type your answer here...'
              className='w-full h-40 p-4 bg-dopage-black text-dopage-lightGreen border border-dopage-tealGray rounded-lg resize-none focus:outline-none focus:border-dopage-primary'
            />
          </KeyboardControl>
        </ControlHighlight>
        {renderRequiredMessage(question)}
      </>
    )
  }

  const renderQuestionInputSectionSingleSelect = (question: SurveyQuestion): ReactElement => {
    if (question.choices === undefined || question.choices.length === 0) {
      return (
        <>
          <div className='text-dopage-disabled'>No choices available</div>
          {renderRequiredMessage(question)}
        </>
      )
    }

    const currentAnswer = answers[question.id]?.choiceIds?.[0] ?? ''
    const isInvalidRequired = question.isRequired && !hasValidAnswerForQuestion(question.id)
    const limitedChoices = question.choices.slice(0, 12)
    const column1 = limitedChoices.slice(0, 6)
    const column2 = limitedChoices.slice(6, 12)

    const handleSelectChange = (choiceId: string): void => {
      updateAnswer(question.id, { choiceIds: [choiceId] })
    }

    const renderChoice = (choice: SurveyQuestionChoice): ReactElement => {
      const inputId = `${getQuestionInputId(question.id)}-${choice.id}`
      const isSelected = currentAnswer === choice.id
      return (
        <div key={choice.id} className='flex items-center gap-3'>
          <HexChoiceButton
            id={inputId}
            label={choice.text}
            isSelected={isSelected}
            isRequired={question.isRequired}
            isInvalid={isInvalidRequired}
            type='radio'
            onClick={() => handleSelectChange(choice.id)}
            layer={kbControlsLayer}
          />
          <span className='text-dopage-lightGreen text-lg'>{choice.text}</span>
        </div>
      )
    }

    return (
      <>
        <div className='text-dopage-tealGray text-sm mb-6'>Select a single answer</div>
        <div role='radiogroup' className='flex gap-6'>
          <div className='flex flex-col gap-5 flex-1'>
            {column1.map(renderChoice)}
          </div>
          {column2.length > 0 && (
            <div className='flex flex-col gap-5 flex-1'>
              {column2.map(renderChoice)}
            </div>
          )}
        </div>
        {renderRequiredMessage(question)}
      </>
    )
  }

  const renderQuestionInputSectionMultiSelect = (question: SurveyQuestion): ReactElement => {
    if (question.choices === undefined || question.choices.length === 0) {
      return (
        <>
          <div className='text-dopage-disabled'>No choices available</div>
          {renderRequiredMessage(question)}
        </>
      )
    }

    const currentAnswer = answers[question.id]?.choiceIds ?? []
    const isInvalidRequired = question.isRequired && !hasValidAnswerForQuestion(question.id)
    const limitedChoices = question.choices.slice(0, 12)
    const column1 = limitedChoices.slice(0, 6)
    const column2 = limitedChoices.slice(6, 12)

    const handleToggleChoice = (choiceId: string): void => {
      const newChoices = currentAnswer.includes(choiceId)
        ? currentAnswer.filter(id => id !== choiceId)
        : [...currentAnswer, choiceId]
      updateAnswer(question.id, { choiceIds: newChoices })
    }

    const renderChoice = (choice: SurveyQuestionChoice): ReactElement => {
      const inputId = `${getQuestionInputId(question.id)}-${choice.id}`
      const isChecked = currentAnswer.includes(choice.id)
      return (
        <div key={choice.id} className='flex items-center gap-3'>
          <HexChoiceButton
            id={inputId}
            label={choice.text}
            isSelected={isChecked}
            isRequired={question.isRequired}
            isInvalid={isInvalidRequired}
            type='checkbox'
            onClick={() => handleToggleChoice(choice.id)}
            layer={kbControlsLayer}
          />
          <span className='text-dopage-lightGreen text-lg'>{choice.text}</span>
        </div>
      )
    }

    return (
      <>
        <div className='text-dopage-tealGray text-sm mb-6'>Select all that apply</div>
        <div role='group' className='flex gap-6'>
          <div className='flex flex-col gap-5 flex-1'>
            {column1.map(renderChoice)}
          </div>
          {column2.length > 0 && (
            <div className='flex flex-col gap-5 flex-1'>
              {column2.map(renderChoice)}
            </div>
          )}
        </div>
        {renderRequiredMessage(question)}
      </>
    )
  }

  const renderQuestionInputSectionLikert = (question: SurveyQuestion): ReactElement => {
    if (question.choices === undefined || question.choices.length === 0) {
      return (
        <>
          <div className='text-dopage-disabled'>No choices available</div>
          {renderRequiredMessage(question)}
        </>
      )
    }

    const currentAnswer = answers[question.id]?.choiceIds?.[0] ?? ''
    const isInvalidRequired = question.isRequired && !hasValidAnswerForQuestion(question.id)
    const choices = question.choices.slice(0, 5) // 5-point Likert scale

    const handleSelectChange = (choiceId: string): void => {
      updateAnswer(question.id, { choiceIds: [choiceId] })
    }

    const renderChoice = (choice: SurveyQuestionChoice): ReactElement => {
      const inputId = `${getQuestionInputId(question.id)}-${choice.id}`
      const isSelected = currentAnswer === choice.id

      return (
        <div key={choice.id} className='flex flex-col items-center gap-1 relative z-10 min-w-[80px]'>
          <HexChoiceButton
            id={inputId}
            label={choice.text}
            isSelected={isSelected}
            isRequired={question.isRequired}
            isInvalid={isInvalidRequired}
            type='radio'
            onClick={() => handleSelectChange(choice.id)}
            layer={kbControlsLayer}
          />
          <span className='text-dopage-lightGreen mt-1 text-xs text-center leading-tight uppercase max-w-[80px] break-words'>
            {choice.text}
          </span>
        </div>
      )
    }

    return (
      <>
        <div className='text-dopage-tealGray text-sm mb-6'>Select one option</div>
        <div
          role='radiogroup'
          className='flex justify-between gap-4 py-6 px-2 relative'
        >
          {/* Connector line going through middle of hexagons */}
          <div className='absolute top-1/2 left-12 right-12 h-0.5 -translate-y-[20px] bg-dopage-tealGray opacity-100' />

          {choices.map(renderChoice)}
        </div>
        {renderRequiredMessage(question)}
      </>
    )
  }

  const renderQuestionInputSection = (question: SurveyQuestion): ReactElement => {
    switch (question.questionType) {
      case SurveyQuestionType.Text:
        return renderQuestionInputSectionText(question)
      case SurveyQuestionType.SingleSelect:
        return renderQuestionInputSectionSingleSelect(question)
      case SurveyQuestionType.MultiSelect:
        return renderQuestionInputSectionMultiSelect(question)
      case SurveyQuestionType.Likert:
        return renderQuestionInputSectionLikert(question)
      default:
        return <div className='text-dopage-disabled'>[Unknown question type]</div>
    }
  }

  return (
    <div className={surveyPageClass}>
      <div className='bg-dopage-charcoalGray w-[600px] h-[750px] flex flex-col relative overflow-hidden'>
        <div className='p-8 flex-1 flex flex-col'>
          <Text
            text={currentQuestion.text}
            size='LG'
            id={getQuestionLabelId(currentQuestion.id)}
            className='text-dopage-lightGreen mb-8'
          />

          <div className='flex-1'>
            {renderQuestionInputSection(currentQuestion)}
          </div>

          <div className='flex justify-center'>
            <ControlHighlight
              active={keyboardControls.isEnabled && keyboardControls.isElementFocused(surveyButtonId)}
              highlightEffect={HighlightEffect.ButtonHexOutline}
            >
              <KeyboardControl layer={kbControlsLayer}>
                <Button
                  id={surveyButtonId}
                  disabled={isNextDisabled}
                  onClick={isLastQuestion ? handleFinish : handleNext}
                  className='px-8 py-3 font-semibold'
                >
                  <span>{isLastQuestion ? 'Finish' : 'Next'}</span>
                </Button>
              </KeyboardControl>
            </ControlHighlight>
          </div>
        </div>

        <div className='h-4 bg-dopage-tealGray w-full'>
          <div
            role='progressbar'
            aria-valuemin={1}
            aria-valuemax={surveyData.questions.length}
            aria-valuenow={currentIndex + 1}
            aria-valuetext={`Question ${currentIndex + 1} of ${surveyData.questions.length}`}
            aria-label='Survey progress'
            className='h-full bg-dopage-primary transition-all duration-300'
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>
    </div>
  )
}

export default Survey
